/**
 * Device storage pressure.
 *
 * When IndexedDB refuses a write for lack of space (QuotaExceededError), the
 * transaction aborts as a whole: the new change is not saved, and everything
 * saved before it — including every queued mutation — is untouched. The
 * person must be told plainly instead of seeing a generic "could not be
 * saved": `guardStorage` flags the condition (shown by the shell banner) and
 * rethrows a `StorageFullError` the screens can recognise.
 */
import { useSyncExternalStore } from 'react';

export class StorageFullError extends Error {
  constructor(override readonly cause: unknown) {
    super('This device is out of storage space.');
    this.name = 'StorageFullError';
  }
}

const QUOTA_NAMES = new Set(['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED']);

/** True for a quota failure however it is wrapped (DOMException, Dexie, nested). */
export function isQuotaError(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== 'object' || depth > 4) return false;
  const candidate = error as {
    name?: unknown;
    code?: unknown;
    inner?: unknown;
    cause?: unknown;
    failures?: unknown;
  };
  if (candidate.name === 'StorageFullError') return true;
  if (typeof candidate.name === 'string' && QUOTA_NAMES.has(candidate.name)) return true;
  // Legacy DOMException codes (22: QUOTA_EXCEEDED_ERR, 1014: Firefox).
  if (error instanceof DOMException && (candidate.code === 22 || candidate.code === 1014)) {
    return true;
  }
  if (isQuotaError(candidate.inner, depth + 1) || isQuotaError(candidate.cause, depth + 1)) {
    return true;
  }
  return Array.isArray(candidate.failures)
    ? candidate.failures.some((failure) => isQuotaError(failure, depth + 1))
    : false;
}

let full = false;
const listeners = new Set<() => void>();

function setFull(next: boolean): void {
  if (full === next) return;
  full = next;
  listeners.forEach((listener) => listener());
}

export function isStorageFull(): boolean {
  return full;
}

export function subscribeStorageFull(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStorageFull(): boolean {
  return useSyncExternalStore(subscribeStorageFull, isStorageFull, isStorageFull);
}

/** Space was freed (a write went through, or the person cleared data). */
export function clearStorageFull(): void {
  setFull(false);
}

/**
 * Run a local write; a quota failure becomes a `StorageFullError` and raises
 * the flag. Chains on the write's own promise rather than adding an `async`
 * layer: inside a Dexie transaction (bulk check-in nests one per worker) an
 * extra native await can let the parent transaction commit early and lose
 * writes.
 */
export function guardStorage<T>(write: () => Promise<T>): Promise<T> {
  return write().then(
    (result) => {
      setFull(false);
      return result;
    },
    (error: unknown) => {
      if (!isQuotaError(error)) throw error;
      setFull(true);
      throw error instanceof StorageFullError ? error : new StorageFullError(error);
    },
  );
}
